export type IncomeBand = 'preferential' | 'general' | 'taxFree' | 'excess';

export interface SavingsScenario {
  monthlyPayment: number;
  months: number;
  principal: number;
  government: number;
  interest: number;
  benefit: number;
  total: number;
}

export type RecommendationReason =
  | 'incomeExcess'
  | 'nearMaturity'
  | 'highCapacity'
  | 'futureHigherReturn'
  | 'leapHigherReturn';

export interface Recommendation {
  winner: 'leap' | 'future';
  reason: RecommendationReason;
}

const LEAP_ACCOUNT = {
  monthlyCap: 70,
  ratePercent: 4.5,
  governmentMonthly: {
    preferential: 3.3,
    general: 2.5,
    taxFree: 2.1,
    excess: 2.1,
  },
} as const;

const FUTURE_SAVINGS = {
  monthlyCap: 50,
  termMonths: 36,
  ratePercent: 7,
  governmentRate: {
    preferential: 0.12,
    general: 0.06,
    taxFree: 0,
    excess: 0,
  },
} as const;

export const NEAR_MATURITY_MONTHS = 24;
export const HIGH_CAPACITY_MONTHLY = 60;

export const resolveIncomeBand = (annualSalary: number): IncomeBand => {
  if (annualSalary <= 3600) {
    return 'preferential';
  }
  if (annualSalary <= 6000) {
    return 'general';
  }
  if (annualSalary <= 7500) {
    return 'taxFree';
  }

  return 'excess';
};

const calculateSavingsInterest = (
  monthly: number,
  months: number,
  annualRatePercent: number,
): number => {
  return (
    monthly * (annualRatePercent / 100 / 12) * ((months * (months + 1)) / 2)
  );
};

const buildScenario = (
  monthly: number,
  months: number,
  government: number,
  ratePercent: number,
): SavingsScenario => {
  const principal = monthly * months;
  const interest = calculateSavingsInterest(monthly, months, ratePercent);
  const benefit = government + interest;

  return {
    monthlyPayment: monthly,
    months,
    principal,
    government,
    interest,
    benefit,
    total: principal + benefit,
  };
};

export const calculateLeapAccount = (
  monthlyPayment: number,
  remainingMonths: number,
  band: IncomeBand,
): SavingsScenario => {
  const monthly = Math.min(monthlyPayment, LEAP_ACCOUNT.monthlyCap);
  const government = LEAP_ACCOUNT.governmentMonthly[band] * remainingMonths;

  return buildScenario(
    monthly,
    remainingMonths,
    government,
    LEAP_ACCOUNT.ratePercent,
  );
};

export const calculateFutureSavings = (
  monthlyPayment: number,
  band: IncomeBand,
): SavingsScenario | null => {
  if (band === 'excess') {
    return null;
  }

  const monthly = Math.min(monthlyPayment, FUTURE_SAVINGS.monthlyCap);
  const government =
    monthly * FUTURE_SAVINGS.governmentRate[band] * FUTURE_SAVINGS.termMonths;

  return buildScenario(
    monthly,
    FUTURE_SAVINGS.termMonths,
    government,
    FUTURE_SAVINGS.ratePercent,
  );
};

export const benefitPerHundred = (scenario: SavingsScenario): number => {
  return Math.round((scenario.benefit / scenario.principal) * 100);
};

export const resolveRecommendation = ({
  remainingMonths,
  monthlyPayment,
  leap,
  future,
}: {
  remainingMonths: number;
  monthlyPayment: number;
  leap: SavingsScenario;
  future: SavingsScenario | null;
}): Recommendation => {
  if (!future) {
    return { winner: 'leap', reason: 'incomeExcess' };
  }
  if (remainingMonths <= NEAR_MATURITY_MONTHS) {
    return { winner: 'leap', reason: 'nearMaturity' };
  }
  if (monthlyPayment >= HIGH_CAPACITY_MONTHLY) {
    return { winner: 'leap', reason: 'highCapacity' };
  }
  if (benefitPerHundred(future) > benefitPerHundred(leap)) {
    return { winner: 'future', reason: 'futureHigherReturn' };
  }

  return { winner: 'leap', reason: 'leapHigherReturn' };
};
